import { useQuery } from '@tanstack/react-query'
import api from '../../../api'
import { cn } from '../../../lib/utils'
import { DataTable } from '../../charts/DataTable'
import { DetailSkeleton } from '../../finance/SkeletonStates'

interface Level2DimensionDrillProps {
  metricCode: string
  dimensionType: string
  dimensionValue: string
  entityCode: string
  periodCode: string
  onDrill: (accountCode: string, label: string) => void
}

export function Level2DimensionDrill({
  metricCode, dimensionType, dimensionValue, entityCode, periodCode, onDrill,
}: Level2DimensionDrillProps) {
  const effectiveEntity = dimensionType === 'entity' ? dimensionValue : entityCode

  const { data, isLoading } = useQuery({
    queryKey: ['dimension-drill', metricCode, effectiveEntity, periodCode],
    queryFn: () => api.get('/financial/trial-balance', {
      params: { entity_code: effectiveEntity, period: periodCode || undefined },
    }).then(r => r.data),
    enabled: !!periodCode,
  })

  const accounts = (data?.accounts || data?.rows || data || [])
    .filter((a: any) => a && a.account_code)

  if (isLoading) return <DetailSkeleton />

  return (
    <div className="space-y-4">
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
      )}>
        <div className="flex items-center gap-1.5 mb-3">
          <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            Accounts — {dimensionValue}
          </span>
        </div>
        {accounts.length > 0 ? (
          <DataTable
            columns={[
              { field: 'account_code', headerName: 'Account', width: 120 },
              { field: 'account_name', headerName: 'Name', flex: 2 },
              { field: 'account_type', headerName: 'Type', width: 100 },
              { field: 'debit_total', headerName: 'Debit', type: 'currency', width: 130 },
              { field: 'credit_total', headerName: 'Credit', type: 'currency', width: 130 },
              { field: 'balance', headerName: 'Balance', type: 'currency', width: 140 },
            ]}
            data={accounts}
            height={Math.min(500, 56 + accounts.length * 42)}
            onRowClick={(row: any) => onDrill(row.account_code, `${row.account_code} — ${row.account_name || 'Account'}`)}
          />
        ) : (
          <div className="text-center py-12 text-[13px] text-[var(--text-muted,#64748B)]">
            No account data for this selection
          </div>
        )}
      </div>
    </div>
  )
}
